import * as React from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Popper,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { useForm, FormContextInterface } from "../context";
import API from "../../service/api";
import { SchoolDetails } from ".";
import { ISchool, schoolDefault } from "../data/formDefault";
import logo from "../../assets/mtech-logo.png";
import "../../index.css";
import VideoPlayer from "./video";
import theme from "../../theme";
interface SchoolRecord {
  id: string;
  label: string;
  icon: string;
  sector: string;
  isActive: boolean;
  signUpMethod: string;
  type: string;
  musicDirector: string;
  quote: string;
  schoolSignUp: boolean;
  showDuration: boolean;
  fees: string;
  feesText: string;
}

export const SchoolName = () => {
  const {
    schoolInfo,
    setSchoolInfo,
    setTermsCondition,
    stepChange,
    handleSubmitLogActivity,
  } = useForm() as FormContextInterface;
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<ISchool[]>([]);

  const loadOptions = async (inputValue: any, callback: any) => {
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_ENDPOINT}application/search-school?term=` +
          inputValue.input +
          "&id=" +
          0
      );
      setOptions(response);
      callback(response);
    } catch (error) {
      console.error(error);
    }
  };

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly SchoolRecord[]) => void
        ) => {
          loadOptions(request, callback);
          setTimeout(() => {
            const elementId = "school-content";
            const getElementById = document.getElementById(elementId);

            if (getElementById) {
              // getElementById.scrollIntoView({ behavior: "smooth" });
              getElementById.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "end",
              });
            }
          }, 200);
        },
        400
      ),
    []
  );

  React.useEffect(() => {
    let active = true;
    if (inputValue?.length > 1) {
      fetch({ input: inputValue }, (results?: any) => {
        if (active) {
          setOptions(results);
        }
      });
    } else {
      setOptions([]);
    }

    return () => {
      active = false;
    };
  }, [schoolInfo, inputValue, fetch]);

  const handleSubmit = async (schoolRecord: SchoolRecord | ISchool | null) => {
    if (schoolRecord?.id) {
      try {
        const response = await API.get(
          `${process.env.REACT_APP_API_ENDPOINT}Application/get-global-extras`
        );
        const activeSchool = await API.get(
          `${process.env.REACT_APP_API_ENDPOINT}School/active-school-by-id?id=${schoolRecord.id}`
        );
        const schoolDetails = {
          id: schoolRecord.id,
          label: schoolRecord.label,
          icon: schoolRecord.icon,
          quote: schoolRecord.quote,
          noticePeriodRequired: activeSchool?.noticePeriodRequired ?? "",
          sector: schoolRecord.sector,
          signUpMethod: schoolRecord.signUpMethod,
          schoolSignUp: schoolRecord.schoolSignUp,
          showDuration: schoolRecord.showDuration,
          fees: schoolRecord.fees,
          schoolFees: schoolRecord.fees,
          feesText: schoolRecord.feesText,
        };
        setSchoolInfo(schoolDetails);
        handleSubmitLogActivity({
          ActivityType: "SchoolSearch",
          SchoolId: schoolRecord.id,
          SchoolName: schoolRecord.label,
        });

        setTermsCondition(response);
        // stepChange("next");
      } catch (error) {
        console.error(error);
      }
    } else {
      setSchoolInfo(schoolDefault);
    }
  };

  const videoJsOptions = {
    autoplay: false,
    controls: true,
    responsive: true,
    fluid: true,
    sources: [
      {
        src: "https://mtech.online/mtech/content/media/One%20I%20Made%20Earlier%202.mp4",
        type: "video/mp4",
      },
    ],
    poster: "https://mtech.online/mtech/content/media/intro.png",
  };

  return (
    <Box id="school-content" mb={3}>
      <Box
        display={"flex"}
        justifyContent="center"
        flexDirection={"column"}
        alignItems="center"
      >
        <Box
          color={"primary.main"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          mb={3}
        >
          <img src={logo} width={"70%"} style={{ maxWidth: "400px" }} />
        </Box>
        <VideoPlayer options={videoJsOptions} />
      </Box>

      {schoolInfo.signUpMethod !== "School" ? (
        <Typography variant="body1" marginY={2} textAlign={"justify"}>
          If your child would like to participate in the M:Tech course, please
          complete the form below.
          {/* There is no commitment at this stage, this is
          purely an expression of interest. */}
        </Typography>
      ) : (
        ""
      )}

      <Typography
        marginY={2}
        className="school_name"
        sx={{
          fontSize: "1.1rem !important",
          fontWeight: "bold",
          paddingTop: "10px",
        }}
      >
        School Name
        <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
          *
        </Box>
      </Typography>
      <Autocomplete
        fullWidth
        forcePopupIcon={false}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        freeSolo
        value={schoolInfo}
        PopperComponent={({ style, ...props }) => (
          <Popper
            {...props}
            style={{ ...style, height: "100px", maxHeight: "180px" }} // width is passed in 'style' prop
          />
        )}
        sx={{
          ".MuiInputBase-root-MuiOutlinedInput-root": {
            color: theme.palette.primary.main,
            "&:hover": {
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
            },
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            "&:hover": {
              borderColor: theme.palette.primary.main,
            },
          },
        }}
        onChange={(event: any, newValue: ISchool | null | any) => {
          handleSubmit(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField {...params} label="Start typing school name:" fullWidth />
        )}
      />

      {schoolInfo.id ? <SchoolDetails /> : <Box height={"200px"}></Box>}
    </Box>
  );
};
